import { Injectable, Type } from '@angular/core';
import { App } from '@df/core/app';
import { TimeoutService } from '@df/ng/timeout.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { ICmsTrayAreaComponent } from './area/icms-tray-area.component';
import { NICmsTray } from './icms-tray.interfaces';

declare module '@df/core/app' {
    interface App {
        iCmsTrayService: ICmsTrayService;
    }
}
@Injectable({ providedIn: 'root' })
export class ICmsTrayService {
    /**
     * Areas availble
     */
    private areas = new Map<string, ICmsTrayAreaComponent>();

    instances$ = new BehaviorSubject<NICmsTray.ITrayInstance<any, any>[]>([]);

    /**
     * Short after tray appears it cannot be closed
     * This is to prevent off clicks to close it immediately
     */
    private protectedInstances = new Set<NICmsTray.ITrayInstance<any, any>>();

    readonly softCloseCheck$ = new Subject<{ instance: NICmsTray.ITrayInstance<any, any>; canClose: boolean }>();

    constructor(
        app: App,
        private timeoutService: TimeoutService
    ) {
        app.iCmsTrayService = this;
    }

    add<D, C extends NICmsTray.ITrayBody<D>>(
        areaId: string,
        tray: NICmsTray.IData<D, C>,
        options: NICmsTray.IOptions
    ): NICmsTray.ITrayInstance<D, C> {
        const instance = this.areas.get(areaId)?.add(tray, options);
        if (instance && !this.protectedInstances.has(instance)) {
            this.protectedInstances.add(instance);
        }
        this.updateInstances();
        this.timeoutService.setTimeout(() => {
            if (instance) {
                this.protectedInstances.delete(instance);
            }
        }, 600);
        return instance!;
    }

    /**
     * @returns true if closing is allowed
     */
    beforeClose<D, C extends NICmsTray.ITrayBody<D>>(instance: NICmsTray.ITrayInstance<D, C>): boolean {
        if (instance.data.onBeforeClose) {
            return instance.data.onBeforeClose(instance);
        }
        return true;
    }

    close<D, C extends NICmsTray.ITrayBody<D>>(instance?: NICmsTray.ITrayInstance<D, C>, soft = false) {
        if (instance && (!soft || !this.protectedInstances.has(instance))) {
            if (this.beforeClose(instance)) {
                instance.componentRef.destroy();
                instance.componentRef.changeDetectorRef.detectChanges();
                this.updateInstances();
            }
        }
    }

    closeAfter(instance: NICmsTray.ITrayInstance<any, any>) {
        const instances = instance.tray.instances;
        const index = instances.indexOf(instance);
        if (index > -1) {
            const nextInstance = instances[index + 1];
            if (nextInstance) {
                this.close(nextInstance);
            }
        }
    }

    closeByArea(areaId: string, soft = false): void {
        const instances = this.areas.get(areaId)?.instances?.filter(i => {
            if (soft) {
                if (this.protectedInstances.has(i)) {
                    return false;
                }
                const event = { canClose: true, instance: i };
                this.softCloseCheck$.next(event);
                return event.canClose;
            }
            return true;
        });
        if (instances?.length) {
            // WARNING: we need to operated on a copy here cause origintal array mutates on destroy
            [...instances].forEach(instance => {
                instance.componentRef.instance.close();
            });
        }
        this.updateInstances();
    }

    closByComponent(component: Type<NICmsTray.ITrayBody<any>>) {
        this.areas.forEach(area => {
            area.instances.forEach(instance => {
                if (instance.data.component === component) {
                    this.close(instance);
                }
            });
        });
    }

    registerArea(areaId: string, area: ICmsTrayAreaComponent): void {
        this.areas.set(areaId, area);
        this.updateInstances();
    }

    unregisterArea(areaId: string): void {
        this.areas.delete(areaId);
        this.updateInstances();
    }

    private updateInstances(): void {
        const instances: NICmsTray.ITrayInstance<any, NICmsTray.ITrayBody<any>>[] = [];
        this.areas.forEach(area => instances.push(...area.instances));
        this.instances$.next(instances);
    }
}
